'use client';

import Box from '@mui/material/Box';
import { type SxProps, type Theme } from '@mui/material/styles';
import type { MouseEvent, ReactNode } from 'react';
import { composeSx } from '../../utils/styledUtils';
import { PhotoTile } from './PhotoTile';

export interface PhotoGridItem {
  id: string;
  src?: string;
  alt: string;
  caption?: ReactNode;
}

export interface PhotoGridProps {
  /** Tiles to render. Missing `src` → empty-state placeholder per tile. */
  items: PhotoGridItem[];
  /** Number of columns. Default 2 (4-up = 2x2). */
  columns?: number;
  /** Aspect ratio for each tile (width/height). Default 1. */
  aspectRatio?: number;
  /** Gap between tiles (theme spacing units). Default 1.5. */
  gap?: number;
  /** Click handler on a tile (receives the item). */
  onTileClick?: (item: PhotoGridItem, event: MouseEvent<HTMLElement>) => void;
  /** Sx overrides on the outer Box. */
  sx?: SxProps<Theme>;
}

export function PhotoGrid({ items, columns = 2, aspectRatio = 1, gap = 1.5, onTileClick, sx }: PhotoGridProps) {
  return (
    <Box sx={composeSx({ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap }, sx)}>
      {items.map((item) => (
        <PhotoTile
          key={item.id}
          src={item.src}
          alt={item.alt}
          caption={item.caption}
          aspectRatio={aspectRatio}
          onClick={onTileClick ? (event) => onTileClick(item, event) : undefined}
        />
      ))}
    </Box>
  );
}

PhotoGrid.displayName = 'PhotoGrid';

export default PhotoGrid;
